import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { subscriptionService } from "../../../services/SubscriptionService";
import { CheckIcon, LightningBoltIcon, SparklesIcon } from "@heroicons/react/outline";
import { Loader2 } from "lucide-react";

const PRO_FEATURES = [
  "AI Event Builder — upload a flyer, fill your form instantly",
  "AI Vendor CRM — smart invites, tags & insights",
  "Unlimited events per month",
  "Attendee ticketing (3.7% + $1.79/ticket)",
  "Document signing & PDF export",
  "3 team seats (owner + 2 contributors)",
  "Priority support",
  "Advanced analytics",
];

const BASIC_FEATURES = [
  "2 published events per month",
  "Unlimited vendor registrations",
  "15% platform fee on vendor fees",
  "No upfront cost",
];

const Subscription = () => {
  const { user, subscriptionTier } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const isPro = subscriptionTier === "pro" || subscriptionTier === "premium";

  useEffect(() => {
    const fetchStatus = async () => {
      if (!user?.uid) return;
      try {
        const result = await subscriptionService.getSubscriptionStatus(user.uid);
        setSubscription(result);
      } catch (err) {
        console.error("Failed to load subscription", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [user?.uid]);

  const handleUpgrade = () => {
    subscriptionService.redirectToPaymentLink(user.uid, "pro", billingCycle);
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    setCancelError(null);
    try {
      await subscriptionService.cancelSubscription(user.uid);
      const result = await subscriptionService.getSubscriptionStatus(user.uid);
      setSubscription(result);
      setShowCancelConfirm(false);
    } catch (err) {
      setCancelError("Failed to cancel. Please try again or contact support.");
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-8">
      {/* Current plan banner */}
      <div className={`rounded-xl border p-6 ${isPro ? "bg-gray-900 border-gray-800 text-white" : "bg-white border-gray-200"}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {isPro ? (
                <SparklesIcon className="h-5 w-5 text-yellow-400" />
              ) : (
                <LightningBoltIcon className="h-5 w-5 text-gray-400" />
              )}
              <span className={`text-xs font-semibold uppercase tracking-wider ${isPro ? "text-yellow-400" : "text-gray-500"}`}>
                Current Plan
              </span>
            </div>
            <h2 className={`text-2xl font-bold ${isPro ? "text-white" : "text-gray-900"}`}>
              {isPro ? "Pro" : "Basic"}
            </h2>
            {isPro && subscription?.currentPeriodEnd && (
              <p className="text-sm text-gray-400 mt-1">
                {subscription.status === "canceled"
                  ? `Access ends ${formatDate(subscription.currentPeriodEnd)}`
                  : `Renews ${formatDate(subscription.currentPeriodEnd)}`}
              </p>
            )}
            {!isPro && (
              <p className="text-sm text-gray-500 mt-1">Free forever — Pebble takes 15% of vendor fees</p>
            )}
          </div>
          {isPro && (
            <span className="px-3 py-1 text-xs font-semibold bg-yellow-400 text-gray-900 rounded-full">
              {subscription?.status === "canceled" ? "Canceling" : "Active"}
            </span>
          )}
        </div>

        {isPro && (
          <div className="mt-6 grid grid-cols-2 gap-2">
            {PRO_FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-gray-300">
                <CheckIcon className="h-4 w-4 text-green-400 flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upgrade section — shown for Basic users */}
      {!isPro && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="bg-gray-900 px-6 py-5 text-white">
            <div className="flex items-center gap-2 mb-1">
              <SparklesIcon className="h-5 w-5 text-yellow-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-yellow-400">Upgrade to Pro</span>
            </div>
            <p className="text-gray-300 text-sm">Unlock ticketing, document signing, analytics, and more.</p>
          </div>

          <div className="p-6">
            {/* Billing toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-6">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${billingCycle === "monthly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${billingCycle === "yearly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >
                Yearly
                <span className="ml-1.5 text-xs font-semibold text-green-600">Save 15%</span>
              </button>
            </div>

            {/* Pricing */}
            <div className="mb-6">
              {billingCycle === "monthly" ? (
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-gray-900">$249</span>
                  <span className="text-gray-500">/month</span>
                </div>
              ) : (
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-gray-900">$211</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">Billed annually at $2,539/year</p>
                </div>
              )}
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
              {PRO_FEATURES.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>

            <button
              onClick={handleUpgrade}
              className="w-full py-3 px-6 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
            >
              Upgrade to Pro — {billingCycle === "monthly" ? "$249/mo" : "$2,539/yr"}
            </button>
            <p className="text-xs text-gray-400 text-center mt-3">
              Secure checkout via Stripe. Cancel anytime.
            </p>
          </div>
        </div>
      )}

      {/* Basic plan comparison — shown for Pro users */}
      {isPro && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Basic Plan (Free)</h3>
          <div className="grid grid-cols-2 gap-2">
            {BASIC_FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-gray-500">
                <CheckIcon className="h-4 w-4 text-gray-300 flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancel section — Pro only */}
      {isPro && subscription?.status === "active" && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Cancel Subscription</h3>
          <p className="text-sm text-gray-500 mb-4">
            You'll keep Pro access until the end of your current billing period. After that, your account reverts to Basic.
          </p>
          {!showCancelConfirm ? (
            <button
              onClick={() => setShowCancelConfirm(true)}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Cancel subscription
            </button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800 mb-3">
                Are you sure? You'll lose access to Pro features at the end of your billing period.
              </p>
              {cancelError && (
                <p className="text-sm text-red-600 mb-3">{cancelError}</p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelSubscription}
                  disabled={canceling}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {canceling && <Loader2 className="h-4 w-4 animate-spin" />}
                  {canceling ? "Canceling..." : "Yes, cancel"}
                </button>
                <button
                  onClick={() => { setShowCancelConfirm(false); setCancelError(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                  Keep Pro
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Subscription;
